import type { SelectChangeEvent } from '@mui/material'
import { DialogContent, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomDialog from '@/components/feedback/CustomDialog'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { useAlertContext } from '@/contexts/AlertContext'
import { GET_RESOURCE_API_ID, GET_RESOURCES_API_ID } from '@/features/resource/endpoints/resources'
import { useUpdateResourceMutation } from '@/features/resource/hooks/useUpdateResourceMutation'
import type { Resource, ResourceDetails, ResourceType } from '@/features/resource/types'
import { getResourceTypeTranslation } from '@/features/resource/utils/resourceTypes'

export type EditResourceModalProps = {
  resourceName?: Resource['resourceName']
  resourceID: Resource['resourceID']
  resourceType?: Resource['resourceType']
  resourceTypeOptions: ResourceType[]
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export type EditResourceFormData = {
  resourceName?: Resource['resourceName']
  resourceType?: Resource['resourceType']
}

const EditResourceModal = ({
  open,
  resourceType,
  resourceName,
  resourceID,
  resourceTypeOptions,
  onClose,
  onSuccess,
}: EditResourceModalProps) => {
  const queryClient = useQueryClient()
  const { pushAlert } = useAlertContext()
  const { t } = useTranslation()
  const { updateResource } = useUpdateResourceMutation(resourceID)

  const form = useForm<EditResourceFormData>({
    values: { resourceName, resourceType },
  })

  /**
   * We are already validating that both fields (resourceName and resourceType) are filled, so
   * that's why we can safely call mutation.mutate here using the exclamation mark.
   *
   * Canonical React Query optimistic-update recipe: cancel any in-flight fetches for the detail
   * query, snapshot the prior cache, write the optimistic patch, then on PATCH failure roll the
   * snapshot back. Without the cancel step, a poll that started before the modal opened (the 5 s
   * `refetchInterval` on useResourceWithPollingQuery clears its timer when `enabled` flips false
   * but does NOT abort already-in-flight fetches) can resolve AFTER setQueryData and overwrite
   * the optimistic name with the stale on-disk one — visible as the name reverting then catching
   * up on the next polling tick.
   *
   * https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates
   */
  async function handleSubmit(data: EditResourceFormData) {
    const payload = {
      resourceType: data.resourceType!,
      resourceName: data.resourceName!,
    }
    const detailQueryKey = [GET_RESOURCE_API_ID, { id: resourceID }] as const

    // Abort any in-flight fetch for the detail query so it can't resolve after setQueryData
    // below and overwrite the optimistic write with stale data.
    await queryClient.cancelQueries({ queryKey: detailQueryKey })
    // Snapshot for rollback if the PATCH fails — we no longer have polling as a passive safety
    // net while the modal is open.
    const previousDetail = queryClient.getQueryData<ResourceDetails>(detailQueryKey)

    // Optimistic write. The BFF PATCH returns 204 only after msa-resource-management accepts,
    // so when the PATCH resolves the optimistic value is guaranteed to match server intent. The
    // BFF's own read replica catches up via Pulsar (sub-second to a few seconds), and the next
    // polling tick reconciles any subsequent server-side change naturally.
    queryClient.setQueryData<ResourceDetails>(detailQueryKey, (prev) =>
      prev
        ? {
            ...prev,
            resourceResponse: {
              ...prev.resourceResponse,
              resourceName: payload.resourceName,
              resourceType: payload.resourceType,
            },
          }
        : prev,
    )

    try {
      await updateResource(payload)
      // The list query (cards) is invalidated separately. Its consumer (the resources list page)
      // typically isn't mounted at the same moment as the detail page, so it doesn't share the
      // in-flight-refetch race, and admins/traders arriving back at the list want it fresh.
      await queryClient.invalidateQueries({ queryKey: [GET_RESOURCES_API_ID] })
      onSuccess()
    } catch (error) {
      // Roll back the optimistic write. previousDetail can be undefined if nothing was cached
      // yet (e.g. the user opened the modal while the very first fetch was still loading); in
      // that case the safest action is to restore that nothing-cached state.
      queryClient.setQueryData<ResourceDetails | undefined>(detailQueryKey, previousDetail)
      // Surface a user-visible alert — the previous behaviour swallowed the failure (console
      // only), leaving the modal open with no signal to the user. Modal stays open by design
      // so the user can retry or copy their input out before cancelling.
      pushAlert({ message: t('resources.edit_modal.save_error'), severity: 'error' })
      console.error('Failed to update resource:', error)
    }
  }

  return (
    <CustomDialog
      aria-modal
      aria-labelledby="edit-resource-dialog-label"
      open={open}
      size="extraSmall"
      title={resourceName!}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Stack sx={{ gap: 3 }}>
              <TextFieldController
                controllerProps={{
                  rules: {
                    required: t('resources.edit_modal.resource_name_field_required_error'),
                  },
                }}
                id="resource-name"
                label={t('resources.edit_modal.resource_name_field_label')}
                name="resourceName"
              />

              <SelectFieldController
                controllerProps={{
                  rules: {
                    required: t('resources.edit_modal.resource_type_field_required_error'),
                  },
                }}
                id="resource-type"
                label={t('resources.edit_modal.resource_type_field_label')}
                name="resourceType"
                options={resourceTypeOptions.map((resourceType) => ({
                  id: resourceType,
                  value: resourceType,
                  label: getResourceTypeTranslation(resourceType, t),
                }))}
                onChange={(evt: SelectChangeEvent<ResourceType>) => {
                  form.setValue('resourceType', evt.target.value as ResourceType)
                }}
              />
            </Stack>
          </DialogContent>

          <FormDialogActions onCancel={onClose} />
        </form>
      </FormProvider>
    </CustomDialog>
  )
}

export default EditResourceModal
